import React, { useState, useEffect, useRef } from 'react';
import { getIsCurrentUserAdminState } from '$/store/profile/selectors';
import { TokenService } from '$/services';
import Modal from '$/components/global/modal';
import LoginForm from '$/pages/login/LoginForm';
import * as Bixby from '$/core';
import { loginExtend, loginModalOpen, loginModalClose } from '$/store/login/actions';
import { connect } from 'react-redux';
import './index.scss';
import { AppState } from '$/store';

interface IProps {
  isAdmin: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  error?: string;
  isRefreshModalOpen: boolean;
  dispatchLoginExtend: (LoginRequest: Bixby.Models.LoginRequest) => Promise<void>;
  dispatchModalOpen: () => void;
  dispatchModalClose: () => void;
}

const RefreshLogin: React.FC<IProps> = ({
  isAdmin,
  dispatchLoginExtend,
  isLoading,
  isRefreshModalOpen,
  error,
  dispatchModalClose,
  dispatchModalOpen,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginInProgress, setLoginInProgress] = useState(false);
  const intervalRef = useRef<any>();

  useEffect(() => {
    const tryDispatchOpen = () => {
      if (!isRefreshModalOpen) {
        dispatchModalOpen();
      }
    };

    const tryDispatchClose = () => {
      if (isRefreshModalOpen) {
        dispatchModalClose();
      }
    };

    intervalRef.current = setInterval(() => {
      const decodedToken = TokenService.getDecodedAccessToken();
      if (!decodedToken || decodedToken.exp < Date.now() / 1000) {
        tryDispatchOpen();
      } else {
        tryDispatchClose();
      }
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [dispatchModalClose, dispatchModalOpen, isRefreshModalOpen]);

  const handleEmailChange = (e: any) => {
    setEmail(e.target.value);
  };
  const handlePasswordChange = (e: any) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoginInProgress(true);
    try {
      await dispatchLoginExtend({
        email,
        password,
      });
      dispatchModalClose();
      setEmail('');
      setPassword('');
    } catch {
      // error is handled via redux
    } finally {
      setLoginInProgress(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Modal active={isRefreshModalOpen} hideClose={true}>
      <div className="refresh-login">
        <h2>Your session has expired.</h2>
        <p>Please login again before continuing.</p>
        <LoginForm
          email={email}
          password={password}
          isLoading={loginInProgress || isLoading}
          onEmailChange={handleEmailChange}
          onPasswordChange={handlePasswordChange}
          onSubmit={handleSubmit}
          errorText={error}
          emailPlaceholder={Bixby.Constants.LOGIN_EMAIL_PLACEHOLDER}
          passwordPlaceholder={Bixby.Constants.LOGIN_PASSWORD_PLACEHOLDER}
        />
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: AppState) => {
  const { isAdmin } = getIsCurrentUserAdminState(state);

  return {
    isAdmin,
    isLoading: state.loginState.isLoading,
    isAuthenticated: state.loginState.authenticated === true,
    error: state.loginState.error,
    isRefreshModalOpen: state.loginState.isRefreshModalOpen,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchLoginExtend: (request: Bixby.Models.LoginRequest) => dispatch(loginExtend(request)),
  dispatchModalOpen: () => dispatch(loginModalOpen()),
  dispatchModalClose: () => dispatch(loginModalClose()),
});

export default connect(mapStateToProps, mapDispatchToProps)(RefreshLogin);
